import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Collapse from '@mui/material/Collapse'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { currency } from '@/v2/utils/format'
import {
  DataTable,
  TableHeader,
  TableHeaderActions,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  EmptyState,
} from '../styles'

const UTMPerformanceSimple = ({ appointmentData, patientData, onExport }) => {
  const [tabValue, setTabValue] = useState(0)
  const [expanded, setExpanded] = useState(true)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const totals = patientData
    ? Object.values(patientData).reduce(
        (acc, curr) => ({
          new_patients: (acc.new_patients || 0) + (curr.new_patients || 0),
          total_appointments:
            (acc.total_appointments || 0) + (curr.total_appointments || 0),
          future_appointments:
            (acc.future_appointments || 0) + (curr.future_appointments || 0),
          showed_up: (acc.showed_up || 0) + (curr.showed_up || 0),
          starts: (acc.starts || 0) + (curr.starts || 0),
          revenue: (acc.revenue || 0) + (curr.revenue || 0),
        }),
        {}
      )
    : {}

  const overallConversion =
    totals.new_patients > 0
      ? ((totals.starts / totals.new_patients) * 100).toFixed(1)
      : 0

  return (
    <DataTable elevation={0}>
      <TableHeader
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme => theme.palette.primary?.dark || '#0056b3',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            sx={{
              color: 'white',
              p: 0.5,
            }}
            onClick={e => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
          >
            {expanded ? (
              <ExpandLessIcon style={{ color: 'white' }} />
            ) : (
              <ExpandMoreIcon style={{ color: 'white' }} />
            )}
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              UTM Performance Analysis
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}
            >
              Track marketing performance across all channels and campaigns
            </Typography>
          </Box>
        </Box>
        <TableHeaderActions onClick={e => e.stopPropagation()}>
          <Typography
            variant="body2"
            sx={theme => ({
              color: theme.palette.common?.white || 'white',
              opacity: 0.9,
              fontWeight: 500,
              mr: 2,
            })}
          >
            {Object.keys(patientData || {}).length} sources •{' '}
            {overallConversion}% avg conversion
          </Typography>
          <Tooltip title="Export to CSV">
            <IconButton
              size="small"
              onClick={onExport}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: '#ffffff1a',
                },
              }}
            >
              <DownloadIcon fontSize="small" style={{ color: 'white' }} />
            </IconButton>
          </Tooltip>
        </TableHeaderActions>
      </TableHeader>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Patient Acquisition" />
            <Tab label="Appointment Performance" />
          </Tabs>
        </Box>

        <Box sx={{ bgcolor: 'white' }}>
          {tabValue === 0 && patientData && (
            <>
              {Object.keys(patientData).length === 0 ? (
                <EmptyState>No patient data available</EmptyState>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <tr>
                        <TableHeaderCell>
                          <Tooltip title="Marketing source or channel">
                            <span>Source</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Number of new patients from this source">
                            <span>New Patients</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Total appointments booked">
                            <span>Total Appts</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Appointments scheduled for future dates">
                            <span>Future</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Patients who attended their appointments">
                            <span>Showed Up</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Patients who started treatment">
                            <span>Starts</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Conversion rate: (Starts ÷ New Patients) × 100">
                            <span>Conv Rate</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>
                          <Tooltip title="Total revenue generated from this source">
                            <span>Revenue</span>
                          </Tooltip>
                        </TableHeaderCell>
                      </tr>
                    </TableHead>
                    <tbody>
                      {Object.entries(patientData).map(([source, data]) => (
                        <TableRow key={source}>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              color:
                                source === 'Direct/Unknown'
                                  ? '#7D7D7D'
                                  : '#0074FE',
                            }}
                          >
                            {source}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.new_patients || 0}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.total_appointments || 0}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                            }}
                          >
                            {data.future_appointments > 0 && (
                              <span
                                style={{ color: '#0074FE', fontWeight: 500 }}
                              >
                                +{data.future_appointments}
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.showed_up || 0}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.starts || 0}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 'bold',
                                color:
                                  data.conversion_rate >= 50
                                    ? '#05AD48'
                                    : data.conversion_rate >= 30
                                    ? '#f08249'
                                    : '#FC7777',
                              }}
                            >
                              {data.conversion_rate || 0}%
                            </span>
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'right',
                              fontWeight: 600,
                              color: data.revenue > 0 ? '#05AD48' : '#7D7D7D',
                            }}
                          >
                            {currency(data.revenue || 0)}
                          </td>
                        </TableRow>
                      ))}
                      <TableRow>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          Total
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          {totals.new_patients}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          {totals.total_appointments}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          {totals.future_appointments}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          {totals.showed_up}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          {totals.starts}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            textAlign: 'center',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 'bold',
                              color:
                                parseFloat(overallConversion) >= 50
                                  ? '#05AD48'
                                  : parseFloat(overallConversion) >= 30
                                  ? '#f08249'
                                  : '#FC7777',
                            }}
                          >
                            {overallConversion}%
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: '#05AD48',
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          {currency(totals.revenue || 0)}
                        </td>
                      </TableRow>
                    </tbody>
                  </Table>
                </Box>
              )}
            </>
          )}

          {tabValue === 1 && appointmentData && (
            <>
              {Object.keys(appointmentData).length === 0 ? (
                <EmptyState>No appointment data available</EmptyState>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <tr>
                        <TableHeaderCell>
                          <Tooltip title="Marketing source or channel">
                            <span>Source</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Total appointments scheduled">
                            <span>Bookings</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Patients who attended their appointments">
                            <span>Showed Up</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Patients who missed their appointments">
                            <span>No Shows</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Percentage of patients who showed up">
                            <span>Show Rate</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Patients who started treatment">
                            <span>Starts</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>
                          <Tooltip title="Conversion rate: (Starts ÷ Bookings) × 100">
                            <span>Conv Rate</span>
                          </Tooltip>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>
                          <Tooltip title="Total revenue generated from this source">
                            <span>Revenue</span>
                          </Tooltip>
                        </TableHeaderCell>
                      </tr>
                    </TableHead>
                    <tbody>
                      {Object.entries(appointmentData).map(([source, data]) => (
                        <TableRow key={source}>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              color:
                                source === 'Direct/Unknown'
                                  ? '#7D7D7D'
                                  : '#0074FE',
                            }}
                          >
                            {source}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.bookings}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.showed_up}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.no_shows}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 'bold',
                                color:
                                  (data.show_rate || 0) > 70
                                    ? '#05AD48'
                                    : '#f08249',
                              }}
                            >
                              {data.show_rate || 0}%
                            </span>
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            {data.starts}
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'center',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 'bold',
                                color:
                                  data.conversion_rate >= 50
                                    ? '#05AD48'
                                    : data.conversion_rate >= 30
                                    ? '#f08249'
                                    : '#FC7777',
                              }}
                            >
                              {data.conversion_rate || 0}%
                            </span>
                          </td>
                          <td
                            style={{
                              padding: '16px 24px',
                              fontSize: '14px',
                              textAlign: 'right',
                              fontWeight: 600,
                              color: data.revenue > 0 ? '#05AD48' : '#7D7D7D',
                            }}
                          >
                            {currency(data.revenue || 0)}
                          </td>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </DataTable>
  )
}

export default UTMPerformanceSimple
